import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, CircularProgress, Typography, Chip, IconButton, ToggleButton, ToggleButtonGroup, Drawer } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchAndFilter from './Common/SearchAndFilter';
import LocationForm from './Locations/LocationForm';
import PersonForm from './People/PersonForm';
import ItemForm from './Items/ItemForm';
import MonsterForm from './Monsters/MonsterForm';
import FactionForm from './Factions/FactionForm';
import LocationDetailPage from './Locations/LocationDetailPage';
import PersonDetailPage from './People/PersonDetailPage';
import ItemDetailPage from './Items/ItemDetailPage';
import MonsterDetailPage from './Monsters/MonsterDetailPage';
import FactionDetailPage from './Factions/FactionDetailPage';
import QuickReferenceCards from './Common/QuickReferenceCards';
import Breadcrumbs from '../Common/Breadcrumbs';
import { wikiTheme } from './wikiTheme';
import * as wikiService from '../../services/wikiService';

/**
 * Unified Wiki page with sidebar
 */
const WikiPage = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Used by Breadcrumbs component via location.state
  const [allEntries, setAllEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [newEntryType, setNewEntryType] = useState('location');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadAllEntries();
  }, []);

  // Check for URL parameter to auto-open an entry
  useEffect(() => {
    if (entryId) {
      setSelectedEntry(entryId);
    }
  }, [entryId]);

  const loadAllEntries = async () => {
    try {
      setLoading(true);
      const entries = await wikiService.getAllWikiEntries();
      setAllEntries(entries);
      setFilteredEntries(entries);

      const tags = await wikiService.getAllTags();
      setSuggestedTags(tags);
    } catch (error) {
      console.error('Error loading entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setTypeFilter(newFilter);
      if (newFilter === 'all') {
        setFilteredEntries(allEntries);
      } else {
        setFilteredEntries(allEntries.filter((entry) => entry.type === newFilter));
      }
    }
  };

  // Function to select an entry and update the URL
  const handleSelectEntry = (entryId) => {
    setSelectedEntry(entryId);
    navigate(`/wiki/${entryId}`);
  };

  // Function to close entry detail and return to list
  const handleCloseEntry = () => {
    setSelectedEntry(null);
    navigate('/wiki');
  };

  const handleCreateClick = (type) => {
    setNewEntryType(type);
    setEditingEntry(null);
    setOpenDialog(true);
  };

  const handleEditClick = (entry) => {
    setEditingEntry(entry);
    setNewEntryType(entry.type);
    setOpenDialog(true);
  };

  const handleDeleteClick = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await wikiService.deleteWikiEntry(entryId);
        loadAllEntries();
        handleCloseEntry();
      } catch (error) {
        console.error('Error deleting entry:', error);
      }
    }
  };

  const handleSuccess = () => {
    setOpenDialog(false);
    setEditingEntry(null);
    loadAllEntries();
    // Trigger a refresh of the detail page if we're viewing one
    setRefreshTrigger(prev => prev + 1);
  };

  const handleFilter = async (filters) => {
    try {
      let filtered = allEntries;

      if (typeFilter !== 'all') {
        filtered = filtered.filter((entry) => entry.type === typeFilter);
      }

      if (filters && filters.tags && filters.tags.length > 0) {
        filtered = filtered.filter((entry) =>
          filters.tags.every((tag) => entry.tags?.includes(tag))
        );
      }

      if (filters && filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filtered = filtered.filter((entry) =>
          entry.title.toLowerCase().includes(searchLower) ||
          entry.description?.toLowerCase().includes(searchLower)
        );
      }

      setFilteredEntries(filtered);
    } catch (error) {
      console.error('Error filtering entries:', error);
    }
  };

  const getFormComponent = () => {
    switch (newEntryType) {
      case 'person':
        return (
          <PersonForm
            onSuccess={handleSuccess}
            existingEntry={editingEntry}
            suggestedTags={suggestedTags}
          />
        );
      case 'item':
        return (
          <ItemForm
            onSuccess={handleSuccess}
            existingEntry={editingEntry}
            suggestedTags={suggestedTags}
          />
        );
      case 'monster':
        return (
          <MonsterForm
            onSuccess={handleSuccess}
            existingEntry={editingEntry}
            suggestedTags={suggestedTags}
          />
        );
      case 'faction':
        return (
          <FactionForm
            onSuccess={handleSuccess}
            existingEntry={editingEntry}
            suggestedTags={suggestedTags}
          />
        );
      default:
        return (
          <LocationForm
            onSuccess={handleSuccess}
            existingEntry={editingEntry}
            suggestedTags={suggestedTags}
          />
        );
    }
  };

  const getDetailComponent = () => {
    if (!selectedEntry) return null;
    const entry = allEntries.find((e) => e.id === selectedEntry);
    if (!entry) return null;

    switch (entry.type) {
      case 'person':
        return (
          <PersonDetailPage
            key={`person-${selectedEntry}-${refreshTrigger}`}
            entryId={selectedEntry}
            onBack={() => {
              handleCloseEntry();
              loadAllEntries();
            }}
            onEdit={() => {
              handleEditClick(entry);
            }}
            onDelete={() => {
              handleDeleteClick(selectedEntry);
            }}
          />
        );
      case 'item':
        return (
          <ItemDetailPage
            key={`item-${selectedEntry}-${refreshTrigger}`}
            entryId={selectedEntry}
            onBack={() => {
              handleCloseEntry();
              loadAllEntries();
            }}
            onEdit={() => {
              handleEditClick(entry);
            }}
            onDelete={() => {
              handleDeleteClick(selectedEntry);
            }}
          />
        );
      case 'monster':
        return (
          <MonsterDetailPage
            key={`monster-${selectedEntry}-${refreshTrigger}`}
            entryId={selectedEntry}
            onBack={() => {
              handleCloseEntry();
              loadAllEntries();
            }}
            onEdit={() => {
              handleEditClick(entry);
            }}
            onDelete={() => {
              handleDeleteClick(selectedEntry);
            }}
          />
        );
      case 'faction':
        return (
          <FactionDetailPage
            key={`faction-${selectedEntry}-${refreshTrigger}`}
            entryId={selectedEntry}
            onBack={() => {
              handleCloseEntry();
              loadAllEntries();
            }}
            onEdit={() => {
              handleEditClick(entry);
            }}
            onDelete={() => {
              handleDeleteClick(selectedEntry);
            }}
          />
        );
      default:
        return (
          <LocationDetailPage
            key={`location-${selectedEntry}-${refreshTrigger}`}
            entryId={selectedEntry}
            onBack={() => {
              handleCloseEntry();
              loadAllEntries();
            }}
            onEdit={() => {
              handleEditClick(entry);
            }}
            onDelete={() => {
              handleDeleteClick(selectedEntry);
            }}
          />
        );
    }
  };

  if (selectedEntry) {
    const entry = allEntries.find((e) => e.id === selectedEntry);
    const getEntryIcon = (type) => {
      switch (type) {
        case 'location': return '📍';
        case 'person': return '👤';
        case 'item': return '⚔️';
        case 'monster': return '👹';
        default: return '📍';
      }
    };
    
    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: wikiTheme.dark }}>
          {/* Breadcrumbs */}
          {entry && (
            <Breadcrumbs 
              currentLabel={entry.title} 
              currentIcon={getEntryIcon(entry.type)} 
            />
          )}
          {/* Detail Content */}
          <Box sx={{ flex: 1 }}>
            {getDetailComponent()}
          </Box>
        </Box>
        
        {/* Create/Edit Dialog - needs to be rendered even on detail page */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: wikiTheme.mediumGray,
            },
          }}
        >
          {getFormComponent()}
        </Dialog>
      </>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: wikiTheme.dark }}>
        <CircularProgress sx={{ color: wikiTheme.primary }} />
      </Box>
    );
  }

  const sidebarWidth = 300;

  // Sidebar content
  const sidebarContent = (
    <Box sx={{ p: 2, backgroundColor: wikiTheme.mediumGray, height: '100%' }}>
      <Typography 
        variant="h6"
        sx={{
          color: wikiTheme.primary,
          fontFamily: wikiTheme.font,
          fontWeight: 'bold',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        ⭐ Recent Entries
      </Typography>

      <QuickReferenceCards 
        limit={6} 
        onSelectEntry={(id) => {
          handleSelectEntry(id);
          setMobileOpen(false);
        }} 
      />

      {/* Overall Relationship Graph Button */}
      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${wikiTheme.lightGray}` }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            navigate('/wiki/relationship-graph');
            setMobileOpen(false);
          }}
          sx={{
            backgroundColor: wikiTheme.primary,
            color: wikiTheme.dark,
            fontFamily: wikiTheme.font,
            fontWeight: 'bold',
            py: 1.5,
            '&:hover': {
              backgroundColor: '#e0a23d',
            },
          }}
        >
          🔗 View All Relationships
        </Button>
      </Box>

      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${wikiTheme.lightGray}` }}>
        <Typography 
          variant="h6"
          sx={{
            color: wikiTheme.primary,
            fontFamily: wikiTheme.font,
            fontWeight: 'bold',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          📊 Statistics
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>Total Entries:</Typography>
            <Chip label={allEntries.length} sx={{ backgroundColor: wikiTheme.primary, color: wikiTheme.dark, fontWeight: 'bold', pointerEvents: 'none' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>📍 Locations:</Typography>
            <Chip label={allEntries.filter((e) => e.type === 'location').length} sx={{ backgroundColor: '#42a5f5', color: '#fff', fontWeight: 'bold', pointerEvents: 'none' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>👥 People:</Typography>
            <Chip label={allEntries.filter((e) => e.type === 'person').length} sx={{ backgroundColor: '#66bb6a', color: '#fff', fontWeight: 'bold', pointerEvents: 'none' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>⚔️ Items:</Typography>
            <Chip label={allEntries.filter((e) => e.type === 'item').length} sx={{ backgroundColor: '#ffa726', color: '#fff', fontWeight: 'bold', pointerEvents: 'none' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>👹 Monsters:</Typography>
            <Chip label={allEntries.filter((e) => e.type === 'monster').length} sx={{ backgroundColor: '#e91e63', color: '#fff', fontWeight: 'bold', pointerEvents: 'none' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: wikiTheme.dark }}>
      {/* Desktop Sidebar */}
      <Box
        sx={{
          width: sidebarWidth,
          backgroundColor: wikiTheme.mediumGray,
          borderRight: `2px solid ${wikiTheme.primary}`,
          display: { xs: 'none', md: 'block' },
          boxShadow: wikiTheme.shadowHeavy,
          position: 'fixed',
          top: '70px',
          left: 0,
          height: 'calc(100vh - 70px)',
          zIndex: 1000,
        }}
      >
        {sidebarContent}
      </Box>

      {/* Mobile Sidebar */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            backgroundColor: wikiTheme.mediumGray,
            width: sidebarWidth,
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Main Content */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        marginLeft: { xs: 0, md: `${sidebarWidth}px` },
      }}>

        {/* Main Content Area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {/* Main Wiki Section */}
          <Box sx={{ backgroundColor: wikiTheme.mediumGray, p: 3, borderRadius: 1, borderTop: `3px solid ${wikiTheme.primary}` }}>
            {/* Controls */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography 
                  variant="body2"
                  sx={{
                    color: wikiTheme.tertiary,
                    fontFamily: wikiTheme.font,
                    mb: 1,
                    fontWeight: 'bold'
                  }}
                >
                  Filter by Type:
                </Typography>
                <ToggleButtonGroup
                  value={typeFilter}
                  exclusive
                  onChange={handleTypeFilterChange}
                  sx={{
                    backgroundColor: wikiTheme.lightGray,
                    '& .MuiToggleButton-root': {
                      color: wikiTheme.tertiary,
                      fontFamily: wikiTheme.font,
                      borderColor: wikiTheme.primary,
                      '&.Mui-selected': {
                        backgroundColor: wikiTheme.primary,
                        color: wikiTheme.dark,
                        '&:hover': {
                          backgroundColor: '#e0a23d',
                        },
                      },
                    },
                  }}
                  size="small"
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="location">📍 Locations</ToggleButton>
                  <ToggleButton value="person">👥 People</ToggleButton>
                  <ToggleButton value="item">⚔️ Items</ToggleButton>
                  <ToggleButton value="monster">👹 Monsters</ToggleButton>
                  <ToggleButton value="faction">🏰 Factions</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Create Buttons */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleCreateClick('location')}
                  sx={{
                    backgroundColor: '#42a5f5',
                    color: '#fff',
                    fontFamily: wikiTheme.font,
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: '#2196F3' },
                  }}
                >
                  + Location
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleCreateClick('person')}
                  sx={{
                    backgroundColor: '#66bb6a',
                    color: '#fff',
                    fontFamily: wikiTheme.font,
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: '#4CAF50' },
                  }}
                >
                  + Person
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleCreateClick('item')}
                  sx={{
                    backgroundColor: '#ffa726',
                    color: '#fff',
                    fontFamily: wikiTheme.font,
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: '#FF9800' },
                  }}
                >
                  + Item
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleCreateClick('monster')}
                  sx={{
                    backgroundColor: '#e91e63',
                    color: '#fff',
                    fontFamily: wikiTheme.font,
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: '#c2185b' },
                  }}
                >
                  + Monster
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleCreateClick('faction')}
                  sx={{
                    backgroundColor: '#ab47bc',
                    color: '#fff',
                    fontFamily: wikiTheme.font,
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: '#9c27b0' },
                  }}
                >
                  + Faction
                </Button>
              </Box>
            </Box>

            {/* Search and Filter */}
            <SearchAndFilter onFilter={handleFilter} />

            {/* Entries List */}
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredEntries.map((entry) => {
                const typeColor =
                  entry.type === 'location'
                    ? '#42a5f5'
                    : entry.type === 'person'
                    ? '#66bb6a'
                    : entry.type === 'item'
                    ? '#ffa726'
                    : entry.type === 'monster'
                    ? '#e91e63'
                    : '#ab47bc';
                const typeEmoji = 
                  entry.type === 'location' 
                    ? '📍' 
                    : entry.type === 'person' 
                    ? '👥' 
                    : entry.type === 'item'
                    ? '⚔️'
                    : entry.type === 'monster'
                    ? '👹'
                    : '🏰';

                return (
                  <Box
                    key={entry.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      backgroundColor: wikiTheme.mediumGray,
                      borderLeft: `4px solid ${typeColor}`,
                      border: '1px solid rgba(176, 141, 87, 0.3)',
                      borderRadius: 1,
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      '&:hover': {
                        backgroundColor: '#3d3d3d',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                      },
                    }}
                    onClick={() => handleSelectEntry(entry.id)}
                  >
                    {/* Left Section: Icon, Image Preview, and Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 250 }}>
                      <Typography variant="h6" sx={{ color: wikiTheme.secondary, fontFamily: wikiTheme.font }}>
                        {typeEmoji}
                      </Typography>
                      
                      {/* Small Image Preview */}
                      {entry.attachments?.some(att => att.type?.startsWith('image/')) && (
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: `2px solid ${typeColor}`,
                            flexShrink: 0,
                            backgroundColor: wikiTheme.dark,
                          }}
                        >
                          <img
                            src={entry.attachments.find(att => att.type?.startsWith('image/')).url}
                            alt={entry.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </Box>
                      )}
                      
                      <Box>
                        <Typography variant="h6" sx={{ color: wikiTheme.secondary, fontFamily: wikiTheme.font, lineHeight: 1.2 }}>
                          {entry.title}
                        </Typography>
                        <Chip
                          label={entry.type}
                          size="small"
                          sx={{
                            backgroundColor: typeColor,
                            color: '#fff',
                            fontFamily: wikiTheme.font,
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            height: '20px',
                            mt: 0.5,
                            pointerEvents: 'none'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Middle Section: Description */}
                    <Box sx={{ flex: 1, minWidth: 0, maxHeight: 40, overflow: 'hidden' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: wikiTheme.tertiary,
                          fontFamily: wikiTheme.font,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: entry.description?.substring(0, 100) || 'No description',
                        }}
                      />
                    </Box>

                    {/* Tags Section */}
                    {entry.tags?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 120 }}>
                        {entry.tags.slice(0, 2).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              backgroundColor: wikiTheme.primary,
                              color: wikiTheme.dark,
                              fontFamily: wikiTheme.font,
                              height: '22px',
                              fontSize: '0.7rem',
                              pointerEvents: 'none'
                            }}
                          />
                        ))}
                        {entry.tags.length > 2 && (
                          <Typography variant="caption" sx={{ color: wikiTheme.tertiary, alignSelf: 'center' }}>
                            +{entry.tags.length - 2}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Relationships Section */}
                    {entry.relationships?.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 100 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: wikiTheme.tertiary, 
                            fontFamily: wikiTheme.font,
                            fontWeight: 'bold',
                          }}
                        >
                          🔗 {entry.relationships.length}
                        </Typography>
                        {entry.relationships.slice(0, 1).map((rel) => {
                          const relatedEntry = allEntries.find(e => e.id === rel.entryId);
                          return (
                            <Chip
                              key={rel.entryId}
                              label={relatedEntry ? relatedEntry.title : 'Unknown'}
                              size="small"
                              sx={{
                                backgroundColor: wikiTheme.dark,
                                color: wikiTheme.secondary,
                                fontFamily: wikiTheme.font,
                                fontSize: '0.65rem',
                                height: '20px',
                                pointerEvents: 'none'
                              }}
                            />
                          );
                        })}
                        {entry.relationships.length > 1 && (
                          <Typography variant="caption" sx={{ color: wikiTheme.tertiary, fontSize: '0.7rem' }}>
                            +{entry.relationships.length - 1}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(entry);
                        }}
                        sx={{
                          color: wikiTheme.primary,
                          '&:hover': {
                            backgroundColor: 'rgba(217, 141, 42, 0.1)',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(entry.id);
                        }}
                        sx={{
                          color: '#ff6b6b',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 107, 107, 0.1)',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {filteredEntries.length === 0 && (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>
                  No entries found
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: wikiTheme.mediumGray,
          },
        }}
      >
        {getFormComponent()}
      </Dialog>
    </Box>
  );
};

export default WikiPage;
